import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  actualTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// TEMPORARY: Force light mode for production until theme system is fully tested
const FORCE_LIGHT_MODE = true;

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (FORCE_LIGHT_MODE) return 'light';
    const stored = localStorage.getItem('godmode_theme');
    return (stored as Theme) || 'system';
  });

  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const root = document.documentElement;

    const applyTheme = (theme: Theme) => {
      // TEMPORARY: Always use light mode for production
      if (FORCE_LIGHT_MODE) {
        setActualTheme('light');
        root.setAttribute('data-theme', 'light');
        root.classList.remove('dark');
        return;
      }

      let isDark = false;

      if (theme === 'system') {
        isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      } else {
        isDark = theme === 'dark';
      }

      setActualTheme(isDark ? 'dark' : 'light');

      // Apply theme using data attribute (better than class for CSS specificity)
      root.setAttribute('data-theme', isDark ? 'dark' : 'light');

      // Also maintain the class for Tailwind dark: utilities and existing code
      if (isDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    applyTheme(theme);

    // Skip system preference listener when forcing light mode
    if (FORCE_LIGHT_MODE) return undefined;

    // Listen for system theme changes
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme(theme);
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    return undefined;
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    // TEMPORARY: Ignore theme changes when forcing light mode
    if (FORCE_LIGHT_MODE) return;
    setThemeState(newTheme);
    localStorage.setItem('godmode_theme', newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, actualTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};